import { isPhoneViewport, openCalFullPage } from "@/lib/cal";
import { useState, type FormEvent } from "react";
import { Phone, Target, ShieldCheck, Users, Star, Rocket } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { supabase } from "@/lib/supabase";
import { submitForm } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import wendelAvatar from "@/assets/wendel-santos-avatar.webp";

type FormData = {
  name: string;
  email: string;
  phone: string;
  website: string;
  instagram_handle: string;
  challenge: string;
};

type Errors = Partial<Record<"name" | "email" | "phone" | "website", string>>;

const CAL_NAMESPACE = "quick-intro-call";
const CAL_LINK = "mind-media/quick-intro-call";
const LOADER_SRC = "https://app.cal.com/embed/embed.js";

function ensureCalLoader() {
  if (typeof window === "undefined") return;
  (function (C: any, A: string, L: string) {
    const p = (a: any, ar: any) => a.q.push(ar);
    const d = C.document;
    C.Cal =
      C.Cal ||
      function () {
        const cal = C.Cal;
        // eslint-disable-next-line prefer-rest-params
        const ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api: any = function () {
            // eslint-disable-next-line prefer-rest-params
            p(api, arguments);
          };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
  })(window, LOADER_SRC, "init");
}

let calInitialized = false;


function openCalWithPrefill(data: FormData) {
  if (typeof window === "undefined") return;
  if (isPhoneViewport()) {
    openCalFullPage(data);
    return;
  }
  ensureCalLoader();
  const Cal = (window as any).Cal;
  if (!Cal) return;
  if (!calInitialized) {
    Cal("init", CAL_NAMESPACE, { origin: "https://app.cal.com" });
    Cal.ns?.[CAL_NAMESPACE]?.("ui", {
      theme: "light",
      hideEventTypeDetails: false,
      layout: "month_view",
    });
    calInitialized = true;
  }
  Cal.ns?.[CAL_NAMESPACE]?.("modal", {
    calLink: CAL_LINK,
    config: {
      layout: "month_view",
      theme: "light",
      useSlotsViewOnSmallScreen: "true",
      name: data.name,
      email: data.email,
      attendeePhoneNumber: data.phone,
      "Website": data.website,
      "Instagram": data.instagram_handle,
      notes: `Challenge: ${data.challenge}`,
    },
  });
}

const initial: FormData = {
  name: "",
  email: "",
  phone: "",
  website: "",
  instagram_handle: "",
  challenge: "",
};

const defaultValueProps = [
  { Icon: Phone, title: "Free strategy call", desc: "30 minutes focused on your growth." },
  { Icon: Target, title: "Custom growth direction", desc: "An actionable plan tailored to your goals." },
  { Icon: ShieldCheck, title: "No commitment required", desc: "Get clarity before you invest." },
];

const defaultStats = [
  { Icon: Users, value: "50+", label: "brands helped" },
  { Icon: Star, value: "5/5", label: "client rating" },
  { Icon: Rocket, value: "24h", label: "Avg. response" },
];

type ValueProp = { Icon: typeof Phone; title: string; desc: string };
type Stat = { Icon: typeof Users; value: string; label: string };

export function ContactSection({
  heading,
  subheading,
  ctaText = "Book a Meeting",
  valueProps = defaultValueProps,
  stats = defaultStats,
  headerFullWidth = false,
  compact = false,
}: {
  heading?: React.ReactNode;
  subheading?: React.ReactNode;
  ctaText?: string;
  valueProps?: ValueProp[];
  stats?: Stat[];
  headerFullWidth?: boolean;
  compact?: boolean;
}) {
  const [form, setForm] = useState<FormData>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  function validate(): boolean {
    const next: Errors = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Please enter a valid email.";
    if (!form.phone.trim()) next.phone = "Please enter your phone number.";
    else if (!isValidPhoneNumber(form.phone)) next.phone = "Please enter a valid phone number.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }


  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    submitForm("contact_book_meeting");
    try {
      const { error } = await supabase.functions.invoke("handle-contact-form", {
        body: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          website: form.website,
          instagram_handle: form.instagram_handle,
          challenge: form.challenge,
        },
      });
      if (error) console.error("handle-contact-form error:", error);
    } catch (err) {
      console.error("handle-contact-form exception:", err);
    }
    openCalWithPrefill(form);
    setForm(initial);
    setErrors({});
    setSubmitting(false);
  }

  const header = (
    <>
      {heading ?? (
        <h2 className="text-5xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl">
          Ready to
          <br />
          <span className="bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent">
            grow your brand?
          </span>
        </h2>
      )}
      {subheading ?? (
        <p className="mt-6 text-lg text-muted-foreground">
          Fill out the form and book a free strategy call.
          <br />
          No pressure, no commitment.
        </p>
      )}
    </>
  );

  return (
    <section id="contact" className={cn("bg-background pb-20 lg:pb-28", compact ? "pt-6 sm:pt-8" : "pt-12 sm:pt-16")}>
      <div className={cn("mx-auto max-w-7xl px-6 lg:px-10", compact ? "mt-6 sm:mt-8" : "mt-12 sm:mt-16")}>
        <div className="rounded-3xl border border-border bg-muted/30 p-6 sm:p-10 lg:p-14">
          {headerFullWidth && (
            <div className="mb-10">
              {header}
            </div>
          )}
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Left */}
            <div className="flex flex-col">
              {!headerFullWidth && header}

              <ul className="mt-10 space-y-6">
                {valueProps.map(({ Icon, title, desc }) => (
                  <li key={title} className="flex items-center gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-border bg-background">
                      <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2} />
                    </span>
                    <div>
                      <p className="font-semibold text-foreground">{title}</p>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-10 grid grid-cols-3 gap-4 border-t border-border pt-8">
                {stats.map(({ Icon, value, label }) => (
                  <div key={label} className="flex flex-col items-center text-center">
                    <Icon className="h-8 w-8 text-brand-blue" />
                    <p className="mt-2 text-2xl font-bold text-foreground">{value}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - form card */}
            <div id="lets-talk" className="scroll-mt-24 rounded-2xl bg-background p-0 sm:border sm:border-border sm:p-8 sm:shadow-sm">
              <div className="mb-6 flex items-center justify-between gap-4">
                <h3 className="text-3xl font-bold tracking-tight text-foreground whitespace-nowrap sm:text-4xl">
                  Let&rsquo;s talk
                </h3>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">Wendel,</p>
                    <p className="text-xs text-muted-foreground">
                      Founder of<br className="sm:hidden" /> Mind Media
                    </p>
                  </div>
                  <img
                    src={wendelAvatar}
                    alt="Wendel Santos"
                    className="avatar-placeholder h-12 w-12 shrink-0 rounded-full object-cover"
                  />
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field
                    label="Name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    error={errors.name}
                  />
                  <Field
                    label="Email"
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@domain.com"
                    error={errors.email}
                  />
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-foreground">
                      Phone Number<span className="text-foreground">*</span>
                    </label>
                    <PhoneInput
                      international
                      defaultCountry="US"
                      value={form.phone}
                      onChange={(value) => setForm((f) => ({ ...f, phone: value ?? "" }))}
                      placeholder="Enter phone number"
                      data-lpignore="true"
                      className={`contact-phone-input flex w-full rounded-lg border bg-background px-4 py-3 text-sm text-foreground focus-within:ring-2 focus-within:ring-brand-blue/40 ${
                        errors.phone ? "border-destructive" : "border-border"
                      }`}
                    />
                    {errors.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
                  </div>
                  <Field
                    label="Website URL"
                    optional
                    value={form.website}
                    onChange={update("website")}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
                <Field
                  label="Instagram Handle"
                  optional
                  value={form.instagram_handle}
                  onChange={update("instagram_handle")}
                  placeholder="@yourhandle"
                />
                <Field
                  label="What's your biggest challenge right now?"
                  optional
                  value={form.challenge}
                  onChange={update("challenge")}
                  placeholder="e.g. Getting more leads, Conversions, Content"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-2 w-full rounded-full bg-primary px-6 py-4 text-base font-semibold text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-60 active:scale-[0.98]"
                >
                  {submitting ? "Submitting…" : ctaText}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  required,
  optional,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
}: {
  label: string;
  required?: boolean;
  optional?: boolean;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-foreground">*</span>}
        {optional && <span className="ml-1 text-muted-foreground">(optional)</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-lpignore="true"
        aria-invalid={!!error}
        className={`w-full rounded-lg border bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-brand-blue/40 ${
          error ? "border-destructive" : "border-border"
        }`}
      />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
